import { NavLink, Outlet, Link } from "react-router-dom";
import { Menu, X, LogOut, LogIn, User as UserIcon, Sparkles, Sun, Moon, UserCircle2 } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/store/AuthContext";
import { useTheme } from "@/store/ThemeContext";
import AdSlot from "@/components/AdSlot";
import TutorialOverlay from "@/components/TutorialOverlay";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const nav = [
  { to: "/", label: "Today", end: true },
  { to: "/library", label: "Habit Library" },
  { to: "/calendar", label: "Group Calendar" },
  { to: "/community", label: "Community" },
  { to: "/premium", label: "Premium" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { user, login, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();

  const displayName = user?.name || "Guest";
  const displayAvatar = user?.picture;
  const displayHandle = user?.email || "not signed in";
  const isPremium = !!user?.is_premium;

  return (
    <div className="min-h-screen bg-paper text-charcoal flex flex-col" data-testid="app-shell">
      <header className="border-b border-stone-200 bg-paper/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <span className="w-9 h-9 rounded-full bg-charcoal text-paper flex items-center justify-center font-serif text-lg leading-none">c</span>
            <span className="font-serif text-2xl leading-none tracking-tight">Cadence</span>
            <span className="eyebrow hidden md:inline">timed habits · together</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-charcoal text-paper"
                      : "text-stone-700 hover:bg-stone-100"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-stone-100 text-stone-600"
              aria-label="Toggle theme"
              data-testid="theme-toggle"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-stone-100 transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    {displayAvatar ? (
                      <img
                        src={displayAvatar}
                        alt={displayName}
                        className="w-8 h-8 rounded-full object-cover border border-stone-200"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <span className="w-8 h-8 rounded-full bg-stone-200 text-stone-600 flex items-center justify-center">
                        <UserIcon size={14} />
                      </span>
                    )}
                    <span className="hidden sm:inline text-sm text-stone-800 max-w-[120px] truncate">
                      {displayName}
                    </span>
                    {isPremium && (
                      <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-950" data-testid="header-pro-badge">
                        <Sparkles size={9} /> Pro
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" data-testid="user-menu-content">
                  <DropdownMenuLabel>
                    <p className="text-sm font-medium text-stone-900 truncate">{displayName}</p>
                    <p className="text-xs text-stone-500 truncate">{displayHandle}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" data-testid="menu-profile-link">
                      <UserCircle2 className="mr-2 h-4 w-4" /> Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                onClick={login}
                className="btn-outline hidden sm:inline-flex min-h-[2.25rem] py-1 text-xs"
                data-testid="signin-btn"
              >
                <LogIn size={14} /> Sign in with Google
              </button>
            )}
            <button
              onClick={() => setOpen((v) => !v)}
              className="md:hidden p-2 rounded-full hover:bg-stone-100"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-stone-200 bg-paper" data-testid="mobile-menu">
            <div className="max-w-6xl mx-auto px-5 py-3 flex flex-col gap-1">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-2xl text-sm font-medium ${
                      isActive ? "bg-charcoal text-paper" : "text-stone-700 hover:bg-stone-100"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              {!user && (
                <button
                  onClick={() => { setOpen(false); login(); }}
                  className="btn-outline mt-2"
                  data-testid="mobile-signin-btn"
                >
                  <LogIn size={14} /> Sign in with Google
                </button>
              )}
              {user && (
                <button
                  onClick={() => { setOpen(false); logout(); }}
                  className="btn-outline mt-2"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut size={14} /> Sign out
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 w-full">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12">
          <Outlet />
        </div>
      </main>

      <footer className="border-t border-stone-200 mt-8">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-6">
          <AdSlot variant="banner" data-testid="footer-ad-banner" />
        </div>
        <div className="max-w-6xl mx-auto px-5 md:px-8 pb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="font-serif text-lg text-stone-700">Cadence &nbsp;·&nbsp; a quiet circle of timed habits.</p>
          <p className="eyebrow">Prototype · mocked data · v0.1</p>
        </div>
      </footer>

      <TutorialOverlay />
    </div>
  );
}
